import React, { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import { Icon } from '@ohif/ui';
import { clearStudiesStorage, useAppConfig } from '@state';

import LoginViewport from '../../components/LoginViewport';
import SvgLoading from '../../components/SvgLoading';
import HttpClient from '../../utils/HttpClient';


export default function SignIn({ setChallenge, setForm }) {
  const { appConfig, setAppConfig } = useAppConfig();
  const cookies = new Cookies(null, { path: '/' });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isFormValid = useMemo(() => {
    return Boolean(
      formData.username.trim() &&
      formData.password
    );
  }, [formData.username, formData.password]);

  const handleInputChange = useCallback((field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!isFormValid) return;

    setIsSubmitting(true);

    try {
      const payload = {
        username: formData.username.trim(),
        password: formData.password,
      };

      const response = await HttpClient.post(
        '/login/',
        payload,
        cookies.get('token'),
        false
      );

      if (!response?.data) {
        toast.error(response?.message || t('Common:Server Error'));
        return;
      }

      if (response.data.challenge) {
        setForm('FirstAccess');
        setChallenge({
          ...response.data,
          username: payload.username
        });
        return;
      }

      try {
        const session = await HttpClient.getSession(navigate);
        if (session?.token) {
          cookies.set('token', session.token);

          setAppConfig({
            ...appConfig,
            session: {
              ...appConfig.session,
              auth: true,
              isStaff: response.data.isStaff || false,
              username: payload.username
            }
          });

          clearStudiesStorage()
          navigate('/', { replace: true });
        } else {
          throw new Error('No token received');
        }
      } catch (sessionError) {
        console.error('Error getting session:', sessionError);
        toast.error(t('Login:Session error. Please try again'));
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || t('Common:Server Error'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    isFormValid,
    cookies,
    navigate,
    t,
    appConfig,
    setAppConfig,
    setForm,
    setChallenge
  ]);

  const FormInput = useCallback(({
    id,
    label,
    type = 'text',
    value,
    onChange,
    showPasswordToggle = false
  }) => (
    <div className="mt-5">
      <label htmlFor={id} className="block text-sm font-medium leading-6 text-white mb-2">
        {label}
      </label>

      <div className="relative">
        <input
          type={type}
          name={id}
          id={id}
          value={value}
          className="block w-full rounded-xl border-0 py-3.5 px-4 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 text-md sm:leading-6 transition-all duration-200"
          onChange={onChange}
          disabled={isSubmitting}
          autoComplete={id === 'email' ? 'username' : 'current-password'}
          required
        />

        {showPasswordToggle && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-2"
            onClick={togglePasswordVisibility}
            disabled={isSubmitting}
            aria-label={showPassword ? t('Login:Hide password') : t('Login:Show password')}
          >
            <Icon
              className="h-5 w-5"
              name={showPassword ? 'eye-hidden' : 'eye-visible'}
            />
          </button>
        )}
      </div>
    </div>
  ), [isSubmitting, showPassword, togglePasswordVisibility, t]);

  return (
    <LoginViewport>
      <div className="w-full">
        <div className="lg:hidden flex justify-center mb-10">
          <img
            className="h-12 w-auto"
            src="/public/assets/logo-harpiAI.svg"
            alt="HarpiAI"
          />
        </div>

        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            {t('Login:Welcome')}
          </h2>

          <p className="mt-2 text-white text-md">
            {t('Login:Sign in to see your studies')}
          </p>
        </div>

        <div className="mt-8">
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-6">
              <FormInput
                id="email"
                label={t('Login:Email/Username')}
                type="text"
                value={formData.username}
                onChange={handleInputChange('username')}
              />

              <FormInput
                id="password"
                label={t('Login:Password')}
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                showPasswordToggle={true}
              />
            </div>

            <div className="mt-8">
              <button
                disabled={isSubmitting || !isFormValid}
                type="submit"
                className="flex w-full justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3.5 text-sm font-semibold text-white shadow-md hover:from-blue-500 hover:to-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform active:scale-[0.98]"
              >
                {isSubmitting ? <SvgLoading /> : t('Login:Sign in')}
              </button>
            </div>

            <p className="mt-6 text-center text-sm text-white">
              {t('Login:Forgot password')}?{' '}
              <Link
                to="/login/forgot-password"
                className="font-semibold text-blue-400 hover:text-blue-300 transition-colors"
              >
                {t('Login:Click here')}
              </Link>
            </p>
          </form>
        </div>
      </div>
    </LoginViewport>
  );
};
